import { useCallback, useMemo } from "react";

import { useUserSetting } from "#app/lib/hooks/use-user-setting";

/**
 * Per-user display order for the entity-view tabs (the Overview/Board/List/
 * Dashboard row on the team and workflow layouts). Persisted in the generic
 * user settings bag (`view-tab-order:<scope>`), so the order follows the user
 * across workspaces. The saved value is normalized against the scope's
 * canonical tab set: unknown and duplicate entries drop, missing tabs append
 * in default order — so a view added later never strands, and a stale saved
 * array can never hide a tab.
 */

export const TEAM_VIEW_TABS = [
  "overview",
  "board",
  "list",
  "dashboard",
] as const;
export type TeamViewTab = (typeof TEAM_VIEW_TABS)[number];

export const WORKFLOW_VIEW_TABS = ["list", "board", "dashboard"] as const;
export type WorkflowViewTab = (typeof WORKFLOW_VIEW_TABS)[number];

function normalizeOrder<T extends string>(
  value: unknown,
  all: readonly T[],
): T[] {
  const canonical = new Set<string>(all);
  const next: T[] = [];
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (
        typeof entry === "string" &&
        canonical.has(entry) &&
        !next.includes(entry as T)
      ) {
        next.push(entry as T);
      }
    }
  }
  for (const tab of all) {
    if (!next.includes(tab)) next.push(tab);
  }
  return next;
}

// Module-level parsers — useUserSetting derives its value off parse identity.
const parseTeamOrder = (value: unknown) => normalizeOrder(value, TEAM_VIEW_TABS);
const parseWorkflowOrder = (value: unknown) =>
  normalizeOrder(value, WORKFLOW_VIEW_TABS);

function useViewTabOrderForKey<T extends string>(
  key: string,
  parse: (value: unknown) => T[],
  pinned?: readonly T[],
) {
  const { value: savedOrder, setValue } = useUserSetting(key, parse);

  // Pinned tabs hoist to the front (in `pinned` sequence) no matter where a
  // previously saved order put them — pinning applied after the fact must
  // win over a stale saved array. The rest keep their saved sequence. Pass
  // `pinned` as a module-level constant so this memo holds.
  const order = useMemo(() => {
    if (!pinned?.length) return savedOrder;
    const pinnedSet = new Set<T>(pinned);
    return [
      ...pinned.filter((tab) => savedOrder.includes(tab)),
      ...savedOrder.filter((tab) => !pinnedSet.has(tab)),
    ];
  }, [savedOrder, pinned]);

  // Reorders arrive from the tab row as the visible SORTABLE subset (pinned
  // tabs are never in the row's `order`, flag-gated tabs are filtered out of
  // the row). Splice the new sequence back into the full order so pinned and
  // hidden tabs keep their slots.
  const reorderVisible = useCallback(
    (nextVisible: string[]) => {
      const visibleSet = new Set(nextVisible);
      let cursor = 0;
      setValue(
        order.map((tab) =>
          visibleSet.has(tab) ? (nextVisible[cursor++] as T) : tab,
        ),
      );
    },
    [order, setValue],
  );

  return { order, reorderVisible };
}

/**
 * `pinned` values always render first (left) and can't be dragged or
 * displaced — pass a module-level constant, and exclude these values from
 * the `order` prop handed to TabsList (membership in that prop is what makes
 * a trigger draggable).
 */
export function useTeamViewTabOrder(pinned?: readonly TeamViewTab[]) {
  return useViewTabOrderForKey<TeamViewTab>(
    "view-tab-order:team",
    parseTeamOrder,
    pinned,
  );
}

/** See useTeamViewTabOrder for the `pinned` contract. */
export function useWorkflowViewTabOrder(pinned?: readonly WorkflowViewTab[]) {
  return useViewTabOrderForKey<WorkflowViewTab>(
    "view-tab-order:workflow",
    parseWorkflowOrder,
    pinned,
  );
}
